import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/authContext';
import { useHubLive } from '../realtime/useDispatcherHub';
import { Button, Icon, Pill, Segmented, type Tone } from '../ui';
import { SEEN_EVENT, seenRelease } from '../whatsnew/seen';
import { useThreads } from '../messages/useThreads';
import { PersonMenu } from './PersonMenu';
import { usePreferences } from './preferencesContext';
import { usePhone } from './softphoneContext';
import { VIEWS, type View } from './views';

/** Which part of the console is showing, for the word beside the firm's name. */
export type Area = 'dispatch' | 'settings' | 'reports' | 'whatsNew';

/**
 * The three-part bar across the top: who this is, which view, and everything else.
 *
 * 48px, fixed, and the only chrome the console has. The middle is empty inside an area,
 * because Desk / Map / Timeline would be three buttons that leave the page somebody is
 * halfway through editing.
 */
export function TopBar({
  area,
  view,
  pinned,
  onPin,
  setupPercent,
}: {
  area: Area;
  /** Absent inside an area, where there is no view to switch. */
  view?: View;
  /** The view that opens at sign-in. */
  pinned: View;
  onPin: () => void;
  /** How far the firm's setup has got, or null for anyone who is not an administrator. */
  setupPercent: number | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branding } = useAuth();
  const { access } = usePreferences();
  const unseen = useUnseenRelease(access.whatsNew);

  return (
    <header className="flex-none h-12 flex items-center justify-between gap-4 px-4 bg-surface border-b border-rule">
      {/* Grows from its content and shrinks with the name, rather than being dealt half
          the bar. Two groups at flex-1 split the width evenly whatever is in them, and the
          right one, justified to its end, spilled leftwards over the chip the moment its
          buttons outgrew its half at tablet width. Sized from content, the name truncates
          and the selects narrow first, and what still does not fit leaves off the right
          edge instead of covering a control. The floor is the mark, the chip and a few
          letters of the name: a truncating child lets the group shrink past its own
          content, and below that floor the chip was pressed under the view switch. Lower
          under lg, where the chip is only the number. */}
      <div className="flex items-center gap-2.5 flex-auto min-w-[120px] lg:min-w-[210px]">
        {branding?.logoUrl != null ? (
          <img src={branding.logoUrl} alt="" className="h-[22px] w-auto shrink-0" />
        ) : (
          <span className="w-[22px] h-[22px] shrink-0 rounded-control bg-brand" />
        )}
        <strong className="font-semibold truncate">
          {branding?.displayName ?? t('app.title')}
        </strong>
        <span className="text-ink-3 truncate">{t(`nav.area.${area}`)}</span>

        {/* For administrators, until the firm is set up. Beside the area word rather than
            among the buttons on the right, where a seventh control overflowed the view
            switch at ordinary widths. A dispatcher never sees it: they can act on none of
            it, and a screen somebody works for eight hours should not carry a number that
            is not theirs. Gone at 100%, the way LinkedIn's meter goes. */}
        {setupPercent !== null && setupPercent < 100 && (
          <Button
            variant="quiet"
            size="sm"
            className="shrink-0"
            onClick={() => void navigate('/settings/setup')}
          >
            <Pill tone="brand" soft>
              {/* The word goes before the number does: on a narrow screen the sidebar
                  entry and the page it opens both say what the percentage is of. */}
              <span className="hidden lg:inline">{t('setup.chip', { percent: setupPercent })}</span>
              <span className="lg:hidden">{t('setup.percent', { percent: setupPercent })}</span>
            </Pill>
          </Button>
        )}
      </div>

      {view !== undefined && (
        <div className="flex items-center gap-1.5">
          <Segmented
            label={t('nav.views')}
            value={view}
            options={VIEWS.map((name) => ({
              value: name,
              label: t(`nav.view.${name}`),
              marked: name === pinned,
            }))}
            onChange={(next) => void navigate(`/${next}`)}
          />

          {/* Filled when this view is the one that opens at sign-in. The dot on the
              segment says which that is; the pin is how it is changed. */}
          <Button
            variant="quiet"
            icon
            onClick={onPin}
            aria-pressed={view === pinned}
            title={view === pinned ? t('nav.pinned') : t('nav.pin')}
            className={view === pinned ? 'text-brand' : ''}
          >
            <Icon name="pin" />
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-initial min-w-0">
        <LivePill />
        <PhonePill />
        <MessagesPill />

        {area !== 'dispatch' && (
          <Button onClick={() => void navigate('/')}>
            <Icon name="back" />
            {t('nav.backToDispatch')}
          </Button>
        )}

        {/* The reports and the firm's settings are the administrator's, and the server
            refuses a dispatcher every page behind them — so a dispatcher's bar has neither.
            Which they are is asked of the server at sign-in, never read off a role here. */}
        {access.reports && area !== 'reports' && (
          <Button onClick={() => void navigate('/reports')}>{t('nav.area.reports')}</Button>
        )}
        {access.settings && area !== 'settings' && (
          <Button onClick={() => void navigate('/settings')}>{t('nav.area.settings')}</Button>
        )}

        {/* The release notes, with a dot until this screen has opened the newest. Quiet,
            because it is never the reason somebody came to the bar. The administrator's, for
            now: its pictures show every screen of the system, most of them ones a dispatcher
            is refused. */}
        {access.whatsNew && area !== 'whatsNew' && (
          <Button
            variant="quiet"
            onClick={() => void navigate('/whats-new')}
            title={unseen ? t('whatsNew.unread') : undefined}
          >
            {t('nav.area.whatsNew')}
            {unseen && (
              <span className="w-[7px] h-[7px] rounded-full bg-brand" aria-hidden="true" />
            )}
          </Button>
        )}

        <PersonMenu />
      </div>
    </header>
  );
}

/**
 * Said only while the live connection is down: the map, the queue and the calls stop
 * changing, and a screen that simply stops looks exactly like a quiet evening.
 *
 * Nothing while it is up. A green "live" at all times would be a pill nobody reads, which is
 * the pill that is not read the one time it turns amber.
 */
function LivePill() {
  const { t } = useTranslation();

  if (useHubLive()) return null;

  // Soft, unlike the telephone's: it has to be noticed from across the room, by somebody
  // who is looking at the map rather than at the bar.
  return (
    <Pill tone="warn" soft dot title={t('app.offlineHint')} className="shrink-0">
      {t('app.offline')}
    </Pill>
  );
}

/**
 * Words from the drivers nobody has read, from whichever view the dispatcher is on.
 *
 * Nothing while there are none, like the live pill: a pill that always shows a zero is a
 * pill that is not read the one time it shows a three. Pressing it goes to the desk, where
 * the threads are.
 */
function MessagesPill() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unread } = useThreads();

  if (unread === 0) return null;

  return (
    <Button variant="quiet" className="shrink-0 px-1" onClick={() => void navigate('/desk')}>
      <Pill tone="brand" dot title={t('messages.unreadHint')}>
        {t('messages.unread', { count: unread })}
      </Pill>
    </Button>
  );
}

/**
 * The telephone's state, at the one place on the screen that is always visible.
 *
 * Nothing at all on a firm with no telephone system, which is most firms for most of this
 * project's life — an error pill on every console would be noise a dispatcher learns to
 * stop seeing, and then does not see the day it means something.
 */
function PhonePill() {
  const { t } = useTranslation();
  const { registration } = usePhone();

  if (registration.state === 'off') return null;
  if (registration.state === 'failed' && registration.code === 'telephony.not_enabled') return null;

  const tone: Tone =
    registration.state === 'registered'
      ? 'ok'
      : registration.state === 'failed'
        ? 'danger'
        : 'warn';

  const words =
    registration.state === 'registered'
      ? t('softphone.registered', { extension: registration.extension })
      : registration.state === 'reconnecting'
        ? t('softphone.reconnecting')
        : registration.state === 'connecting'
          ? t('softphone.connecting')
          : t(`errors.${registration.code}`, { defaultValue: t('softphone.failed') });

  return (
    <Pill tone={tone} dot>
      {words}
    </Pill>
  );
}

/**
 * Whether the newest release notes are still unread on this screen.
 *
 * Nothing at all for somebody the notes are not for — not even the request: which release
 * is the newest is learned by loading the notes, so it is loaded only when `enabled`, as its
 * own file, and never by a dispatcher's browser. Then kept in step by the event the notes
 * page fires when it is opened, and by the storage event another tab on the same PC fires,
 * so the dot goes the moment the notes are read, wherever they were read.
 */
function useUnseenRelease(enabled: boolean): boolean {
  const [latest, setLatest] = useState<string | null>(null);
  const [seen, setSeen] = useState(seenRelease);

  useEffect(() => {
    if (!enabled) return undefined;

    let live = true;

    void import('../whatsnew/releases').then(({ LATEST }) => {
      if (live) setLatest(LATEST.id);
    });

    return () => {
      live = false;
    };
  }, [enabled]);

  useEffect(() => {
    const sync = () => setSeen(seenRelease());

    globalThis.addEventListener(SEEN_EVENT, sync);
    globalThis.addEventListener('storage', sync);

    return () => {
      globalThis.removeEventListener(SEEN_EVENT, sync);
      globalThis.removeEventListener('storage', sync);
    };
  }, []);

  return enabled && latest !== null && seen !== undefined && seen !== latest;
}
